import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import ProductForm, { ModifierGroupRef, ProductDraft, draftFromProduct, draftToPayload, productErrorMessage } from './ProductForm';
import { ProductMediaManager } from './ProductMedia';
import { SectionCard, StatCard, EmptyState, ConfirmDialog, currency, number } from './ui';
import { TRUE_COLORS } from '../theme';
import { Category, Supplier } from '../types';

/** Dialog to add a branch-specific price or a supplier-specific cost. */
const OverrideDialog = ({
  open, onClose, title, optionLabel, options, amountLabel, onSave,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  optionLabel: string;
  options: { id: number; name: string }[];
  amountLabel: string;
  onSave: (optionId: number, amount: number) => Promise<void>;
}) => {
  const [option, setOption] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [error, setError] = React.useState('');
  React.useEffect(() => { if (open) { setOption(''); setAmount(''); setError(''); } }, [open]);

  const save = async () => {
    if (!option || amount === '') { setError('Completa ambos campos.'); return; }
    try {
      await onSave(Number(option), Number(amount));
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(typeof data === 'object' && data ? Object.values(data).flat().join(' ') : 'No se pudo guardar.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label={optionLabel} value={option} onChange={e => setOption(e.target.value)} fullWidth>
            {options.length === 0 && <MenuItem disabled value="">Sin opciones disponibles</MenuItem>}
            {options.map(o => <MenuItem key={o.id} value={String(o.id)}>{o.name}</MenuItem>)}
          </TextField>
          <TextField
            label={amountLabel}
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            fullWidth
            slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button onClick={save} variant="contained">Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" spacing={2} sx={{ py: 1.25, borderBottom: `1px solid ${TRUE_COLORS.borderSolid}`, '&:last-child': { borderBottom: 0 } }}>
    <Typography variant="body2" sx={{ color: 'text.secondary', width: 160, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary', minWidth: 0, wordBreak: 'break-word' }}>{value || '—'}</Box>
  </Stack>
);

const ProductDetail = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { id } = useParams();
  const mediaFailed: string[] = (useLocation().state as any)?.mediaFailed ?? [];

  const [product, setProduct] = React.useState<any>(null);
  const [inventory, setInventory] = React.useState<any[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [suppliers, setSuppliers] = React.useState<Supplier[]>([]);
  const [locations, setLocations] = React.useState<any[]>([]);
  const [modifierGroups, setModifierGroups] = React.useState<ModifierGroupRef[]>([]);
  const [notFound, setNotFound] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<ProductDraft | null>(null);
  const [saveError, setSaveError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [confirm, setConfirm] = React.useState<null | 'deactivate' | 'delete'>(null);
  const [priceDialog, setPriceDialog] = React.useState(false);
  const [costDialog, setCostDialog] = React.useState(false);

  React.useEffect(() => {
    axios.get(`api/products/${id}/`)
      .then(r => setProduct(r.data))
      .catch(() => setNotFound(true));
    axios.get('inventory/inventory/', { params: { product_id: id } }).then(r => setInventory(r.data ?? [])).catch(() => {});
  }, [axios, id, refresh]);

  React.useEffect(() => {
    axios.get('api/categories/').then(r => setCategories(r.data ?? [])).catch(() => {});
    axios.get('inventory/suppliers/').then(r => setSuppliers(r.data ?? [])).catch(() => {});
    axios.get('api/locations/').then(r => setLocations(r.data ?? [])).catch(() => {});
    axios.get('api/modifier-groups/').then(r => setModifierGroups(r.data ?? [])).catch(() => {});
  }, [axios]);

  const startEdit = () => { setDraft(draftFromProduct(product)); setSaveError(''); setEditing(true); };

  const saveEdit = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`api/products/${id}/`, draftToPayload(draft));
      setProduct(data);
      setEditing(false);
    } catch (err) {
      setSaveError(productErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async () => {
    setMenuAnchor(null);
    // PUT re-enables the product; send the full payload so validation passes.
    const { data } = await axios.put(`api/products/${id}/`, draftToPayload(draftFromProduct(product)));
    setProduct(data);
  };

  if (notFound) {
    return (
      <PageShell back={{ to: '/admin/products', label: 'Productos' }}>
        <EmptyState title="Producto no encontrado" description="Puede que haya sido eliminado." />
      </PageShell>
    );
  }

  if (editing && draft) {
    return (
      <PageShell
        back={{ to: `/admin/products/${id}`, label: product?.name ?? 'Producto' }}
        title="Editar producto"
        actions={
          <>
            <Button color="inherit" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button variant="contained" onClick={saveEdit} disabled={saving}>Guardar cambios</Button>
          </>
        }
      >
        <ProductForm
          draft={draft}
          onChange={setDraft}
          categories={categories}
          suppliers={suppliers}
          error={saveError}
          lockBarcode
          media={id && <ProductMediaManager axios={axios} productId={id} />}
          modifierGroups={modifierGroups}
        />
      </PageShell>
    );
  }

  const price = Number(product?.price) || 0;
  const cost = Number(product?.cost) || 0;
  const margin = price > 0 ? (price - cost) / price : null;
  const totalStock = inventory.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0);
  const usedLocations = new Set((product?.location_specific_prices ?? []).map((l: any) => l.location?.id));
  const usedSuppliers = new Set((product?.supplier_specific_costs ?? []).map((s: any) => s.supplier?.id));

  return (
    <PageShell
      back={{ to: '/admin/products', label: 'Productos' }}
      title={product ? product.name : <Skeleton width={280} />}
      subtitle={
        product && (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }} component="span">
            <Chip
              size="small"
              label={product.enabled ? 'Activo' : 'Inactivo'}
              sx={product.enabled ? { bgcolor: 'rgba(46,125,91,0.12)', color: TRUE_COLORS.success } : undefined}
            />
            {product.enabled && !product.display && <Chip size="small" variant="outlined" label="Oculto en POS" />}
            {product.enabled && product.online_visible === false && <Chip size="small" variant="outlined" label="Oculto en catálogo" />}
            {product.enabled && product.sold_out && <Chip size="small" label="Agotado" sx={{ bgcolor: 'rgba(192,57,43,0.1)', color: TRUE_COLORS.danger }} />}
            {product.enabled && product.coming_soon && <Chip size="small" variant="outlined" label="Próximamente" />}
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
              Código {product.barcode}
            </Typography>
          </Stack>
        )
      }
      actions={
        product && (
          <>
            {product.enabled ? (
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={startEdit}>Editar</Button>
            ) : (
              <Button variant="contained" startIcon={<RestoreIcon />} onClick={reactivate}>Reactivar</Button>
            )}
            <IconButton onClick={e => setMenuAnchor(e.currentTarget)} aria-label="Más acciones" sx={{ border: `1px solid ${TRUE_COLORS.borderSolid}` }}>
              <MoreVertIcon />
            </IconButton>
          </>
        )
      }
    >
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {product?.enabled ? (
          <MenuItem onClick={() => { setMenuAnchor(null); setConfirm('deactivate'); }}>
            <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon> Desactivar producto
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { setMenuAnchor(null); setConfirm('delete'); }} sx={{ color: TRUE_COLORS.danger }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: TRUE_COLORS.danger }} /></ListItemIcon> Eliminar permanentemente
          </MenuItem>
        )}
      </Menu>

      <Grid container spacing={3}>
        {[
          { label: 'Precio de venta', value: currency(price), icon: <SellOutlinedIcon /> },
          { label: 'Costo', value: currency(cost), icon: <PaymentsOutlinedIcon /> },
          { label: 'Margen', value: margin == null ? '—' : `${(margin * 100).toFixed(1)}%`, icon: <PercentIcon /> },
          { label: 'Stock total', value: `${number(totalStock, 2)} uds.`, icon: <Inventory2OutlinedIcon /> },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
            <StatCard label={card.label} value={card.value} icon={card.icon} loading={!product} />
          </Grid>
        ))}

        {mediaFailed.length > 0 && (
          <Grid size={12}>
            <Alert severity="warning">Producto guardado, pero no se subieron: {mediaFailed.join(', ')}. Agrégalos en Fotos y videos.</Alert>
          </Grid>
        )}

        <Grid size={12}>
          <SectionCard title="Fotos y videos" subtitle="Se muestran en tu catálogo en línea. Los cambios se guardan al instante.">
            {id && <ProductMediaManager axios={axios} productId={id} />}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <SectionCard title="Detalles">
            {product ? (
              <Box>
                <DetailRow label="Descripción" value={product.description} />
                <DetailRow label="SKU" value={product.sku} />
                <DetailRow
                  label="Categorías"
                  value={product.category?.length
                    ? <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>{product.category.map((c: Category) => <Chip key={c.id} size="small" label={c.name} />)}</Stack>
                    : null}
                />
                <DetailRow label="Proveedores" value={product.suppliers?.map((s: Supplier) => s.name).join(', ')} />
                <DetailRow label="Opciones al vender" value={(product.modifier_groups ?? []).map((gid: number) => modifierGroups.find(g => g.id === gid)?.name).filter(Boolean).join(', ')} />
                <DetailRow label="Visible en POS" value={product.display ? 'Sí' : 'No'} />
                <DetailRow label="Catálogo en línea" value={product.online_visible === false ? 'Oculto' : product.coming_soon ? 'Próximamente' : product.sold_out ? 'Visible, agotado' : 'Visible'} />
                <DetailRow label="Creado" value={product.created_at && dayjs(product.created_at).format('D MMM YYYY, HH:mm')} />
                <DetailRow label="Última modificación" value={product.last_modified && dayjs(product.last_modified).fromNow()} />
              </Box>
            ) : <Skeleton variant="rounded" height={260} />}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard title="Inventario por sucursal" subtitle="Existencias actuales y punto de reorden" noPadding>
            {inventory.length === 0 ? (
              <EmptyState compact title="Sin inventario registrado" description="Crea una sucursal o registra movimientos desde Inventario." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sucursal</TableCell>
                    <TableCell align="right">Existencias</TableCell>
                    <TableCell align="right">Reorden</TableCell>
                    <TableCell align="right">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory.map(item => {
                    const qty = Number(item.quantity) || 0;
                    const restock = Number(item.restock_quantity) || 0;
                    const low = restock > 0 && qty <= restock;
                    return (
                      <TableRow key={item.id}>
                        <TableCell sx={{ fontWeight: 500 }}>{item.location?.name}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{number(qty, 2)}</TableCell>
                        <TableCell align="right">{restock ? number(restock, 2) : '—'}</TableCell>
                        <TableCell align="right">
                          {qty <= 0
                            ? <Chip size="small" label="Agotado" sx={{ bgcolor: 'rgba(192,57,43,0.1)', color: TRUE_COLORS.danger }} />
                            : low
                              ? <Chip size="small" label="Bajo" sx={{ bgcolor: 'rgba(183,121,31,0.12)', color: TRUE_COLORS.warning }} />
                              : <Chip size="small" label="OK" sx={{ bgcolor: 'rgba(46,125,91,0.12)', color: TRUE_COLORS.success }} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Precios por sucursal"
            subtitle="Sobrescriben el precio general en una sucursal"
            action={product?.enabled && <Button size="small" startIcon={<AddIcon />} onClick={() => setPriceDialog(true)}>Agregar</Button>}
            noPadding
          >
            {(product?.location_specific_prices ?? []).length === 0 ? (
              <EmptyState compact title="Mismo precio en todas las sucursales" />
            ) : (
              <Table size="small">
                <TableBody>
                  {product.location_specific_prices.map((lsp: any) => (
                    <TableRow key={lsp.id}>
                      <TableCell sx={{ fontWeight: 500 }}>{lsp.location?.name}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{currency(lsp.price)}</TableCell>
                      <TableCell align="right" sx={{ width: 48 }}>
                        <Tooltip title="Quitar">
                          <IconButton size="small" onClick={async () => { await axios.delete(`api/location-specific-prices/${lsp.id}/`); setRefresh(r => r + 1); }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard
            title="Costos por proveedor"
            subtitle="Lo que te cobra cada proveedor por este producto"
            action={product?.enabled && <Button size="small" startIcon={<AddIcon />} onClick={() => setCostDialog(true)}>Agregar</Button>}
            noPadding
          >
            {(product?.supplier_specific_costs ?? []).length === 0 ? (
              <EmptyState compact title="Sin costos por proveedor" />
            ) : (
              <Table size="small">
                <TableBody>
                  {product.supplier_specific_costs.map((ssc: any) => (
                    <TableRow key={ssc.id}>
                      <TableCell sx={{ fontWeight: 500 }}>{ssc.supplier?.name}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{currency(ssc.cost)}</TableCell>
                      <TableCell align="right" sx={{ width: 48 }}>
                        <Tooltip title="Quitar">
                          <IconButton size="small" onClick={async () => { await axios.delete(`inventory/supplier-specific-costs/${ssc.id}/`); setRefresh(r => r + 1); }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <OverrideDialog
        open={priceDialog}
        onClose={() => setPriceDialog(false)}
        title="Precio por sucursal"
        optionLabel="Sucursal"
        options={locations.filter(l => !usedLocations.has(l.id))}
        amountLabel="Precio"
        onSave={async (location, price) => {
          await axios.post('api/add-location-specific-price/', { location, product: id, price });
          setRefresh(r => r + 1);
        }}
      />
      <OverrideDialog
        open={costDialog}
        onClose={() => setCostDialog(false)}
        title="Costo por proveedor"
        optionLabel="Proveedor"
        options={suppliers.filter(s => !usedSuppliers.has(s.id))}
        amountLabel="Costo"
        onSave={async (supplier, cost) => {
          await axios.post('inventory/add-supplier-specific-cost/', { supplier, product: id, cost });
          setRefresh(r => r + 1);
        }}
      />

      <ConfirmDialog
        open={confirm === 'deactivate'}
        title="¿Desactivar este producto?"
        description="Dejará de aparecer en TRUE POS. Su historial de ventas se conserva y puedes reactivarlo cuando quieras."
        confirmLabel="Desactivar"
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          await axios.delete(`api/products/${id}/`);
          setConfirm(null);
          setRefresh(r => r + 1);
        }}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        danger
        title="¿Eliminar permanentemente?"
        description="Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          await axios.delete('api/deleted-products/', { data: { id } });
          navigate('/admin/products', { replace: true });
        }}
      />
    </PageShell>
  );
};

export default ProductDetail;
